// One review-comment thread card: header (author/date + resolved badge +
// count), the anchor quote (or anchor-lost indicator), the nested replies, a
// reply composer (open threads only), and a resolve/reopen toggle.
//
//   §9 — logical RTL utilities; every BE free-text string (`authorName`,
//        `body`, `anchorText`) gets `bidiProps` (it can be Arabic in an FR
//        chrome). Dates are formatted with `Intl.DateTimeFormat(i18n.language)`,
//        not concatenated. Counts use i18next `count` interpolation.
//   W1 — no business logic: resolution state + thread ordering come from the
//        BE; the card only projects + dispatches port calls via callbacks.
//   W6 — no $/tokens.

import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { bidiProps } from '@/lib/bidi'
import type { CommentThread } from '@/domain/comments/entity'

interface CommentCardProps {
  thread: CommentThread
  language: string
  onReply: (threadId: string, body: string) => void
  onToggleResolve: (threadId: string, isResolved: boolean) => void
}

/** Locale-aware date+time. Invalid/empty ISO → empty string. Presentation
 *  only. */
function formatDate(iso: string, language: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  return new Intl.DateTimeFormat(language || 'fr', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(d)
}

export function CommentCard({
  thread,
  language,
  onReply,
  onToggleResolve,
}: CommentCardProps): JSX.Element {
  const { t } = useTranslation()
  const [draft, setDraft] = useState('')

  const submitReply = (): void => {
    const body = draft.trim()
    if (!body) return
    onReply(thread.id, body)
    setDraft('')
  }

  return (
    <li
      data-testid={`comments-thread-${thread.id}`}
      className="flex flex-col gap-2 border-b border-ink/[0.06] px-3.5 py-3"
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-2">
        <div className="flex min-w-0 flex-col">
          <span
            className="truncate text-xs font-semibold text-ink"
            {...bidiProps(thread.authorName)}
          >
            {thread.authorName}
          </span>
          <span className="mt-0.5 text-[10px] text-ink/50">
            {t('comments.thread.meta', {
              date: formatDate(thread.createdAt, language),
            })}
          </span>
        </div>
        {thread.isResolved && (
          <span
            data-testid={`comments-thread-resolved-${thread.id}`}
            className="shrink-0 rounded-full bg-faits-bg px-2 py-0.5 text-[10px] font-medium text-faits-deep"
          >
            {t('comments.thread.resolvedBadge')}
          </span>
        )}
      </div>

      {/* Anchor */}
      {thread.anchorLost ? (
        <div
          data-testid={`comments-thread-anchor-lost-${thread.id}`}
          className="border-s-2 border-warn ps-2 text-[11px] italic text-warn"
        >
          {t('comments.thread.anchorLost')}
        </div>
      ) : (
        thread.anchorText && (
          <blockquote
            data-testid={`comments-thread-anchor-${thread.id}`}
            className="border-s-2 border-ink/15 ps-2 text-[11px] italic text-ink/70"
            {...bidiProps(thread.anchorText)}
          >
            {t('comments.thread.anchorQuote', { text: thread.anchorText })}
          </blockquote>
        )
      )}

      {/* Replies */}
      {thread.comments.length > 0 && (
        <ul className="flex flex-col gap-1.5">
          {thread.comments.map((c) => (
            <li
              key={c.id}
              data-testid={`comments-comment-${c.id}`}
              className="rounded-md bg-ink/[0.03] px-2.5 py-1.5"
            >
              <div className="flex items-center justify-between gap-2">
                <span
                  className="truncate text-[11px] font-medium text-ink/80"
                  {...bidiProps(c.authorName)}
                >
                  {c.authorName}
                </span>
                <span className="shrink-0 text-[10px] text-ink/40">
                  {formatDate(c.createdAt, language)}
                </span>
              </div>
              <p
                className="mt-0.5 text-xs leading-relaxed text-ink/80"
                {...bidiProps(c.body)}
              >
                {c.body}
              </p>
            </li>
          ))}
        </ul>
      )}

      {/* Footer: count + resolve/reopen toggle */}
      <div className="flex items-center justify-between gap-2">
        <span className="text-[10px] text-ink/40">
          {t('comments.thread.count', { count: thread.commentCount })}
        </span>
        <button
          type="button"
          data-testid={`comments-thread-toggle-${thread.id}`}
          onClick={() => onToggleResolve(thread.id, !thread.isResolved)}
          className="shrink-0 rounded px-2 py-1 text-[10px] font-medium text-ink/60 hover:bg-ink/5"
        >
          {thread.isResolved
            ? t('comments.thread.reopen')
            : t('comments.thread.resolve')}
        </button>
      </div>

      {/* Reply composer — open threads only */}
      {!thread.isResolved && (
        <div className="flex items-end gap-1.5">
          <textarea
            data-testid={`comments-reply-input-${thread.id}`}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            rows={2}
            placeholder={t('comments.reply.placeholder')}
            className="min-w-0 flex-1 resize-none rounded-md border border-ink/15 px-2 py-1 text-xs text-ink focus:border-ink focus:outline-none"
          />
          <button
            type="button"
            data-testid={`comments-reply-send-${thread.id}`}
            onClick={submitReply}
            disabled={draft.trim().length === 0}
            className="shrink-0 rounded-md bg-ink px-2.5 py-1.5 text-[11px] font-medium text-white disabled:cursor-not-allowed disabled:opacity-40"
          >
            {t('comments.reply.send')}
          </button>
        </div>
      )}
    </li>
  )
}
